<script setup lang="ts">
import type { InputPhoneProps } from '.';
import type { Input } from '../ui/input';
import type { CountryPhoneData } from '~/core/ports/entities/phone';
import type { Directive } from 'vue';

import { cn } from '@/utils';
import {
	countriesPhoneDataByDDI,
	countriesPhoneDataList
} from '~/utils/phone/countries';
import { Mask } from 'maska';
import { vMaska as vMaskaDirective } from 'maska/vue';
import { nextTick } from 'vue';

import Select from '../ui/select/Select.vue';
import SelectContent from '../ui/select/SelectContent.vue';
import SelectGroup from '../ui/select/SelectGroup.vue';
import SelectItem from '../ui/select/SelectItem.vue';
import SelectTrigger from '../ui/select/SelectTrigger.vue';
import SelectValue from '../ui/select/SelectValue.vue';
import messages from './InputPhone.locale.json';

// Diretiva registrada localmente (o plugin global punha a maska no entry de
// todas as paginas; ela so e usada aqui, dentro do chunk lazy da modal).
const vMaska = vMaskaDirective as unknown as Directive;

const props = withDefaults(defineProps<InputPhoneProps>(), {
	disabled: false,
	required: false,
	autofocus: false,
	cyTestId: ''
});

defineEmits<{
	(e: 'blur'): void;
}>();

const strings = messages.pt;

const modelValue = defineModel<InputPhoneProps['modelValue']>({
	required: true
});

const iso = shallowRef('BR');
const number = shallowRef('');
const maskedNumber = ref<string>('');
const phoneInput = useTemplateRef<typeof Input>('phoneInput');

// Flag para evitar loops de atualização durante processamento
const isUpdatingInternally = ref(false);

const country = computed<CountryPhoneData | null>(
	() => countriesPhoneDataByDDI[iso.value] || null
);
const mask = computed(() => country.value?.mask);

// Função para detectar o país baseado no número completo
function detectCountryFromPhoneNumber(phoneNumber: string): string {
	if (!phoneNumber) return 'BR';

	// Procura pelo país que tem o código que corresponde ao início do número
	for (const countryData of countriesPhoneDataList) {
		if (phoneNumber.startsWith(countryData.code)) {
			return countryData.ddi;
		}
	}

	return 'BR'; // fallback para Brasil
}

watch([iso, number], ([_newIso, newNumber]) => {
	if (country.value && !isUpdatingInternally.value) {
		const newModelValue = country.value.code + newNumber;

		if (modelValue.value === newModelValue) return;

		isUpdatingInternally.value = true;
		modelValue.value = newModelValue;
		nextTick(() => {
			isUpdatingInternally.value = false;
		});
	}
});

watch(
	modelValue,
	(newValue, oldValue) => {
		if (isUpdatingInternally.value) return;

		if (newValue === oldValue) return;

		if (!newValue) {
			if (!oldValue || (oldValue === '' && !number.value)) {
				iso.value = 'BR';
				number.value = '';
				maskedNumber.value = '';
			}
			return;
		}

		const detectedCountryDDI = detectCountryFromPhoneNumber(newValue);

		if (iso.value !== detectedCountryDDI) {
			isUpdatingInternally.value = true;
			iso.value = detectedCountryDDI;
			nextTick(() => {
				isUpdatingInternally.value = false;
			});
		}

		nextTick(() => {
			if (isUpdatingInternally.value) return;

			const countryCode = country.value?.code;

			if (!countryCode) return;

			const newPhoneNumber = newValue.replace(countryCode, '') || '';

			if (number.value === newPhoneNumber) return;

			const maskObject = new Mask({ mask: mask.value });

			isUpdatingInternally.value = true;
			number.value = newPhoneNumber;
			maskedNumber.value = maskObject.masked(newPhoneNumber);
			nextTick(() => {
				isUpdatingInternally.value = false;
			});
		});
	},
	{ immediate: true }
);

const veeValidateAttrs = computed<
	Pick<InputPhoneProps, 'onBlur' | 'onChange' | 'onInput' | 'required'>
>(() => ({
	onBlur: props.onBlur,
	onChange: props.onChange,
	onInput: props.onInput,
	required: props.required
}));

defineExpose({ number });
</script>

<template>
	<div
		:class="[
			cn('flex gap-px rounded-[2px] border border-white-dark-4', wrapperClass),
			{ 'border-error!': error },
			{ 'border-primary!': phoneInput?.focused },
			{ 'border-white-dark-3! bg-white-dark-2': disabled }
		]"
	>
		<Select
			v-model="iso"
			data-testid="select-phone"
			:class="cn('h-[50px] items-center border-none px-3')"
			:disabled="disabled"
		>
			<SelectTrigger
				:class="
					cn(
						'h-[50px] w-min min-w-[95px] items-center border-none py-2 pl-3 pr-2 shadow-none',
						disabled ? 'bg-white-dark-2' : 'bg-secondary-dark-1',
						selectTriggerClass
					)
				"
				:arrow-class="
					cn(disabled ? 'text-primary/40' : 'text-primary', selectArrowClass)
				"
			>
				<div :class="cn('flex flex-col', selectPlaceholderClasses?.wrapper)">
					<SelectValue>
						<div
							:class="cn('flex items-center gap-1', selectItemClasses?.wrapper)"
						>
							<img
								:src="country?.flag"
								loading="lazy"
								:alt="`${country?.name} Flag`"
								:class="cn('h-3 w-[18px]', selectItemClasses?.flag)"
							/>
							<span
								:class="
									cn(
										'text-sm font-normal leading-[150%]',
										disabled ? 'text-primary/40' : 'text-primary',
										selectItemClasses?.label
									)
								"
							>
								{{ country?.code }}
							</span>
						</div>
					</SelectValue>
				</div>
			</SelectTrigger>

			<SelectContent class="bg-white" :avoid-collisions="false">
				<SelectGroup>
					<SelectItem
						v-for="item in countriesPhoneDataList"
						:key="`country-phone-option:${item.name}`"
						:value="item.ddi"
						class="data-highlighted:bg-white-dark-3"
					>
						<div
							:class="cn('flex items-center gap-1', selectItemClasses?.wrapper)"
						>
							<img
								:src="item.flag"
								loading="lazy"
								:alt="`${item.name} Flag`"
								:class="cn('h-3 w-[18px]', selectItemClasses?.flag)"
							/>
							<p
								:class="
									cn(
										'text-sm font-normal leading-[150%] text-primary',
										selectItemClasses?.label
									)
								"
							>
								{{ item.code }}
							</p>
						</div>
					</SelectItem>
				</SelectGroup>
			</SelectContent>
		</Select>

		<Input
			ref="phoneInput"
			v-model="maskedNumber"
			v-maska:number.unmasked="mask"
			:autofocus="autofocus"
			:cy-test-id="cyTestId"
			type="text"
			:placeholder="strings.input.placeholder"
			:class="
				cn(
					'focus-visible:ring-none h-[50px] w-full border-none px-4 focus-visible:ring-0 disabled:bg-white-dark-2',
					inputClass
				)
			"
			:disabled="disabled"
			v-bind="veeValidateAttrs"
			@blur="$emit('blur')"
		/>
	</div>
</template>
